import InviteMemberIcon from "assets/img/Sidebar/invite-member.svg";
import React from "react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import plusIcon from "../../assets/img/icons/plus.svg";
import Email from "./Email";
import Help from "./Help";
import { TooltipPortal } from "./TooltipPortal";
import Update from "./Update";
import { useTooltip } from "./hooks/useTooltip";

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 6px 0 0 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: ${({ sidebarMini }) => (sidebarMini ? "4px" : "4px 8px")};
  background: none;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: background 0.15s;
  min-width: 0;
  min-height: 0;
  line-height: 1.2;

  &:hover {
    background: ${({ theme }) => theme.sidebar.hoverBgColor};
  }
`;

const ActionIcon = styled.div`
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  img,
  svg {
    filter: brightness(0) saturate(100%) invert(27%) sepia(11%) saturate(1622%)
      hue-rotate(180deg) brightness(97%) contrast(89%);
  }
`;

const ActionText = styled.span`
  ${({ sidebarMini }) =>
    sidebarMini &&
    `
      display: none;
    `}
`;

const PopoverMenu = styled.div`
  position: absolute;
  bottom: 36px;
  right: -70px;
  min-width: 130px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(32, 190, 173, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(32, 190, 173, 0.1);
  border-radius: 8px;
  padding: 4px 0;
  z-index: 9999; /* Very high z-index to appear above sidebar */
  display: flex;
  flex-direction: column;
  animation: fadeInUp 0.16s;
  backdrop-filter: blur(8px); /* Add subtle blur effect */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PopoverMenuItem = styled.button`
  background: none;
  border: none;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #34495e;
  cursor: pointer;
  transition: background 0.13s;
  border-radius: 4px;
  text-decoration: none; /* Remove underline for anchor tags */
  &:hover {
    background: #f6f8f9;
    color: #34495e; /* Maintain consistent color on hover */
    text-decoration: none; /* Ensure no underline on hover */
  }
  &:focus {
    outline: none;
    background: #f6f8f9;
  }
  &:visited {
    color: #34495e; /* Maintain color for visited links */
  }
`;

const PopoverIcon = styled.div`
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  img,
  svg {
    filter: brightness(0) saturate(100%) invert(27%) sepia(11%) saturate(1622%)
      hue-rotate(180deg) brightness(97%) contrast(89%);
  }
`;

const HelpButtonWrapper = styled.div`
  position: relative;
`;

const StickyActionsSection = ({
  history,
  sidebarMini,
  selectedOrganization,
  handleNavOpen,
}) => {
  const inviteTooltip = useTooltip();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const helpBtnRef = useRef();
  const popoverRef = useRef();

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return;
    function handleClick(e) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        helpBtnRef.current &&
        !helpBtnRef.current.contains(e.target)
      ) {
        setPopoverOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [popoverOpen]);

  const handleInviteClick = () => {
    history.push("/user/member-invitation");
  };

  const handleHelpClick = () => {
    window.open("https://apploye.com/help", "_blank");
  };

  // Don't show for clients
  if (selectedOrganization?.role === "client") {
    return null;
  }

  return (
    <ActionsRow>
      {(selectedOrganization?.role === "owner" ||
        selectedOrganization?.role === "admin") && (
        <TooltipPortal
          text="Invite Member"
          isVisible={sidebarMini && inviteTooltip.isVisible}
          targetRef={inviteTooltip.targetRef}
        >
          <ActionButton
            sidebarMini={sidebarMini}
            onClick={() => {
              handleInviteClick();
              document.documentElement.classList.toggle("nav-open");
              handleNavOpen();
            }}
            ref={inviteTooltip.targetRef}
            onMouseEnter={inviteTooltip.showTooltip}
            onMouseLeave={inviteTooltip.hideTooltip}
            aria-label="Invite Member"
          >
            <ActionIcon>
              <img
                src={InviteMemberIcon}
                alt="Invite Member"
                width="20"
                height="20"
              />
            </ActionIcon>
            <ActionText sidebarMini={sidebarMini}>Invite</ActionText>
          </ActionButton>
        </TooltipPortal>
      )}
      <HelpButtonWrapper>
        <ActionButton
          sidebarMini={sidebarMini}
          onClick={() => setPopoverOpen(v => !v)}
          ref={helpBtnRef}
          aria-label="Help & Resources"
        >
          <ActionIcon>
            <Help size={20} />
          </ActionIcon>
          <ActionText sidebarMini={sidebarMini}>Support</ActionText>
        </ActionButton>
        {popoverOpen && (
          <PopoverMenu ref={popoverRef}>
            <PopoverMenuItem
              onClick={() => {
                setPopoverOpen(false);
                document.documentElement.classList.toggle("nav-open");
                handleNavOpen();
                window.ProductLift?.openWidget(
                  process.env.REACT_APP_PRODUCTLIFT_WIDGET_ID,
                );
              }}
            >
              <PopoverIcon>
                <Update size={15} />
              </PopoverIcon>
              What's New?
            </PopoverMenuItem>
            <PopoverMenuItem
              as="a"
              href="https://www.fieldservicely.com/help"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setPopoverOpen(false);
                document.documentElement.classList.toggle("nav-open");
                handleNavOpen();
              }}
            >
              <PopoverIcon>
                <Help size={15} />
              </PopoverIcon>
              Need Help?
            </PopoverMenuItem>
            <PopoverMenuItem
              as="a"
              href="mailto:support@fieldservicely.com"
              onClick={() => {
                setPopoverOpen(false);
                document.documentElement.classList.toggle("nav-open");
                handleNavOpen();
              }}
            >
              <PopoverIcon>
                <Email size={15} />
              </PopoverIcon>
              Contact Us
            </PopoverMenuItem>
          </PopoverMenu>
        )}
      </HelpButtonWrapper>
    </ActionsRow>
  );
};

export default StickyActionsSection;
